import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Globe, ChevronDown } from 'lucide-react';
import { type Office, type Department, type Organization } from '@/types/org';
import { type UserProfile } from '@/types/team';

function getContextTitle(
    activeDepartment: Department | undefined,
    activeOffice: Office | undefined,
): string {
    if (activeDepartment) return activeDepartment.name;
    if (activeOffice) return activeOffice.name;
    return 'Overview';
}

function getContextSubtitle(activeDepartment: Department | undefined, activeOfficeId: string | null): string {
    if (activeDepartment) return 'Department View';
    if (activeOfficeId) return 'Office View';
    return 'Global Overview';
}

interface SidebarContextSwitcherProps {
    organization: Organization | null;
    offices: Office[];
    departments: Department[];
    activeOfficeId: string | null;
    activeDepartmentId: string | null;
    setActiveDepartmentId: (id: string | null) => void;
    userRole?: string;
    userProfile?: UserProfile | null;
    isCollapsed?: boolean;
    /** WEB-111: 'bar' is the compact switcher in the desktop top bar (one line, title only, menu drops below). */
    variant?: 'rail' | 'bar';
}

export const SidebarContextSwitcher: React.FC<SidebarContextSwitcherProps> = ({
    organization,
    offices,
    departments,
    activeOfficeId,
    activeDepartmentId,
    setActiveDepartmentId,
    userRole,
    userProfile,
    isCollapsed,
    variant = 'rail',
}) => {
    const bar = variant === 'bar';
    const [isOpen, setIsOpen] = useState(false);
    const [expandedOfficeId, setExpandedOfficeId] = useState<string | null>(null);
    const navigate = useNavigate();
    const activeOffice = offices.find(o => o.id === activeOfficeId);
    const activeDepartment = departments.find(d => d.id === activeDepartmentId);

    // Track the last seen activeOfficeId to know when to auto-expand
    const [lastActiveOfficeId, setLastActiveOfficeId] = useState<string | null>(null);

    if (activeOfficeId !== lastActiveOfficeId) {
        setLastActiveOfficeId(activeOfficeId);
        if (activeOfficeId) {
            setExpandedOfficeId(activeOfficeId);
        }
    }

    const handleSwitch = (officeId: string | null, departmentId: string | null = null) => {
        if (officeId) {
            setActiveDepartmentId(departmentId);
            if (departmentId) {
                navigate(`/office/${officeId}/department/${departmentId}`);
            } else {
                navigate(`/office/${officeId}/dashboard`);
            }
        } else {
            setActiveDepartmentId(null);
            navigate('/'); // Global Dashboard
        }
        setIsOpen(false);
    };

    const toggleExpansion = (officeId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedOfficeId(prev => prev === officeId ? null : officeId);
    };

    const renderOfficeContent = (o: Office) => {
        let officeDepts = departments.filter(d => d.officeId === o.id);

        if ((userRole === 'MEMBER' || userRole === 'DEPT_MANAGER') && userProfile?.departmentId) {
            officeDepts = officeDepts.filter(d => d.id === userProfile.departmentId);
        }

        return (
            <div className="pl-3 mt-1 mb-2">

                {officeDepts.map(d => (
                    <button
                        key={d.id}
                        onClick={() => handleSwitch(o.id, d.id)}
                        className={`w-full p-2.5 flex items-center gap-2.5 rounded-lg cursor-pointer text-left transition-all duration-200 border ${activeDepartmentId === d.id
                            ? 'bg-accent-electric/10 border-accent-electric/30 text-accent-electric shadow-[0_0_10px_rgb(var(--accent-electric-rgb)/0.1)]'
                            : 'bg-transparent border-transparent text-text-secondary'
                            }`}
                    >
                        <div
                            className={`w-1.5 h-1.5 rounded-full bg-current ${activeDepartmentId === d.id ? 'opacity-100' : 'opacity-50'}`}
                        />
                        <span className={`text-xs ${activeDepartmentId === d.id ? 'font-bold' : 'font-medium'}`}>{d.name}</span>
                    </button>
                ))}
            </div>
        );
    };

    // FINANCE (WEB-75) has no office or department to switch between; its
    // routes are all global.
    const isRestricted = userRole === 'MEMBER' || userRole === 'DEPT_MANAGER' || userRole === 'FINANCE';
    // WEB-111: in the top bar the global perspective names the org, which the removed header row used to show.
    const contextTitle = bar && !activeOffice && !activeDepartment ? (organization?.name || 'Global Overview') : getContextTitle(activeDepartment, activeOffice);
    const contextSubtitle = getContextSubtitle(activeDepartment, activeOfficeId);

    return (
        <div className={bar ? 'relative shrink-0' : 'relative mb-8'}>
            <button
                onClick={() => !isRestricted && setIsOpen(!isOpen)}
                title={isCollapsed || bar ? `${contextTitle} (${contextSubtitle})` : undefined}
                aria-label={bar ? `Perspective: ${contextTitle}` : undefined}
                aria-expanded={bar ? isOpen : undefined}
                className={bar
                    ? `h-9 max-w-[14rem] px-2.5 rounded-xl border border-divider-subtle flex items-center gap-2 text-left text-sm font-bold text-text-primary bg-[rgba(255,255,255,0.03)] ${isOpen ? 'border-accent-electric' : ''} ${isRestricted ? 'cursor-default opacity-80' : 'cursor-pointer hover:bg-surface-hover'}`
                    : `w-full p-3 bg-[rgba(255,255,255,0.03)] rounded-2xl border border-divider-subtle flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} transition-all duration-200 text-left ${isOpen ? 'bg-[rgba(255,255,255,0.08)] border-accent-electric' : ''
                    } ${isRestricted ? 'cursor-default opacity-80' : 'cursor-pointer'}`}
            >
                {bar ? (
                    <>
                        {activeOfficeId ? <MapPin size={16} className="shrink-0 text-accent-electric" /> : <Globe size={16} className="shrink-0 text-accent-electric" />}
                        <span className="truncate">{contextTitle}</span>
                        {!isRestricted && <ChevronDown size={14} className={`shrink-0 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />}
                    </>
                ) : (<>
                <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-accent-electric/10 text-accent-electric shadow-[0_0_15px_rgb(var(--accent-electric-rgb)/0.2)]"
                >
                    {activeOfficeId ? <MapPin size={18} /> : <Globe size={18} />}
                </div>
                {!isCollapsed && (
                    <>
                        <div className="flex-1 overflow-hidden">
                            <div className="text-[0.8125rem] font-bold text-text-primary whitespace-nowrap overflow-hidden text-ellipsis">
                                {contextTitle}
                            </div>
                            <div className="text-[0.6875rem] text-text-muted font-medium">
                                {contextSubtitle}
                            </div>
                        </div>
                        {!isRestricted && (
                            <ChevronDown size={14} className={`text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                        )}
                    </>
                )}
                </>)}
            </button>

            {isOpen && (
                <>
                    <div
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 z-40"
                    />
                    <div className={`absolute top-[calc(100%+8px)] left-0 ${isCollapsed || bar ? 'w-64' : 'right-0'} bg-bg-primary/95 backdrop-blur-xl rounded-2xl border border-divider-subtle p-2 z-50 shadow-2xl animate-[fadeInScale_0.2s_ease-out] max-h-[400px] overflow-y-auto`}>
                        <div className="text-[0.65rem] font-bold text-text-muted py-2 px-3 uppercase tracking-wider">
                            Switch Perspective
                        </div>

                        {(userRole === 'OWNER' || userRole === 'ORG_ADMIN' || offices.length > 1) ? (
                            <>
                                {(userRole === 'OWNER' || userRole === 'ORG_ADMIN') && (
                                    <button
                                        onClick={() => handleSwitch(null)}
                                        className={`w-full p-2.5 flex items-center gap-2.5 bg-transparent border rounded-lg text-text-secondary cursor-pointer text-left transition-all duration-200 hover:bg-surface-hover hover:text-text-primary ${!activeOfficeId
                                            ? 'bg-accent-electric/10 border-accent-electric/50 text-accent-electric shadow-[0_0_10px_rgb(var(--accent-electric-rgb)/0.15)] font-bold'
                                            : 'border-transparent'
                                            }`}
                                    >
                                        <Globe size={14} />
                                        <span className="text-[0.8125rem] font-semibold">Global Overview</span>
                                    </button>
                                )}
                                <div className="h-px bg-surface-active my-1" />

                                {offices.map(o => {
                                    const isExpanded = expandedOfficeId === o.id;
                                    const isActiveOffice = activeOfficeId === o.id;

                                    return (
                                        <div key={o.id}>
                                            <div
                                                className={`w-full p-2.5 flex items-center justify-between bg-transparent border rounded-lg text-text-secondary cursor-pointer text-left transition-all duration-200 hover:bg-surface-hover hover:text-text-primary ${isActiveOffice
                                                    ? 'bg-accent-electric/10 border-accent-electric/50 text-accent-electric shadow-[0_0_10px_rgb(var(--accent-electric-rgb)/0.15)] font-bold'
                                                    : 'border-transparent'
                                                    }`}
                                                onClick={() => handleSwitch(o.id, null)}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <MapPin size={14} />
                                                    <span className="text-[0.8125rem] font-semibold">{o.name}</span>
                                                </div>

                                                <div
                                                    onClick={(e) => toggleExpansion(o.id, e)}
                                                    className="p-1 -m-1 flex items-center justify-center rounded"
                                                >
                                                    <ChevronDown size={12} className={`opacity-70 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                                </div>
                                            </div>
                                            {isExpanded && renderOfficeContent(o)}
                                        </div>
                                    );
                                })}
                            </>
                        ) : (
                            activeOffice && (
                                <>
                                    {/* Link back to Office Overview (Pulse) */}
                                    <button
                                        onClick={() => handleSwitch(activeOffice.id, null)}
                                        className={`w-full p-2.5 flex items-center gap-2.5 mb-2 rounded-lg cursor-pointer text-left transition-all duration-200 border ${!activeDepartmentId
                                            ? 'bg-accent-electric/10 border-accent-electric/50 text-accent-electric shadow-[0_0_10px_rgb(var(--accent-electric-rgb)/0.15)] font-bold'
                                            : 'bg-transparent border-transparent text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                                            }`}
                                    >
                                        <MapPin size={14} />
                                        <span className="text-[0.8125rem] font-semibold">{activeOffice.name} (Overview)</span>
                                    </button>

                                    {/* Departments List */}
                                    <div className="px-1">
                                        <div className="text-[0.6rem] font-bold text-text-muted uppercase tracking-wider mb-1 pl-2">Departments</div>
                                        {renderOfficeContent(activeOffice)}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
